// apps/client/src/pages/GamePage.tsx
//
// Main game page — V2 Beelucky Sweepstakes
// Golden Paths 2+3: Core loop + Rescue flow
//
// Features:
// - Lobby: Chest/Key ad bundle, single-ad cards, BingoChips redeem
// - Rescue: Bottom Sheet when 0 BingoChips
// - Auto-transition: WAITING→PLAYING (no manual button)
// - Post-game loop: "Prepare Next Game" → back to lobby
// - Near-win: golden border + "Almost there!" tag
//

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// removed icCoin
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';
import { useUserStore } from '../store/userStore';
import { Bingo90Strip, BingoCard, BallDisplay, GameTimer } from '../components';
import { socketClient } from '../lib/socket';
import { Button } from '../ui-kit/Button';
import { BottomNav } from '../ui-kit/Layouts';
import { ExitGameModal } from '../ui-kit/ExitGameModal';
import { BottomSheet } from '../ui-kit/BottomSheet';
import { ChestUnlock } from '../ui-kit/ChestUnlock';
import { Toast, useToast } from '../ui-kit/Toast';
import Icon from '../ui-kit/Icon';
import { WinModal, GlobalWinToast } from '../ui-kit/Feedback';
import { PerfectCoin } from '../ui-kit/OnboardingSVGs';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';

type ViewType = 'home' | 'game' | 'missions' | 'store' | 'referrals' | 'sunday' | 'profile';

interface GamePageProps {
    onNavigate: (view: ViewType) => void;
}

export function GamePage({ onNavigate }: GamePageProps) {
    const { user, isAuthenticated } = useAuthStore();
    const {
        sessionId,
        status,
        type,
        timeUntilStart,
        drawnNumbers,
        lastDrawnNumber,
        tickets,
        playerCount,
        lineAwarded,
        twoLinesAwarded,
        dailyTimer,
        fetchGameState,
        buyTicket,
        fetchMyTickets,
        joinGame,
        // Card acquisition from store (persists across navigation)
        earnedCards,
        bundleProgress,
        isConnected,
        bundleUsedThisSession,
        addEarnedCards,
        resetEarnedCards,
        setBundleProgress,
        markBundleUsed,
    } = useGameStore();

    const { chips, refreshBalance, fetchMissions, watchAd, adWatchingPlacement, settings } = useUserStore();
    const { t } = useTranslation();

    const [showExitModal, setShowExitModal] = useState(false);
    const [showWinModal, setShowWinModal] = useState(false);
    const [winAmount, setWinAmount] = useState(0);
    const [winType, setWinType] = useState('');
    const [globalToast, setGlobalToast] = useState({ message: '', prize: 0, isVisible: false });
    const [missedGame, setMissedGame] = useState(false);
    const [showRescueSheet, setShowRescueSheet] = useState(false);
    const [chestJustCompleted, setChestJustCompleted] = useState(false);

    // Toast
    const { toast, showToast, dismissToast } = useToast();

    // Animated chips
    const animatedChips = useAnimatedCounter(chips);

    // Near-win detection based on active phase
    const nearWinTicketIds = useMemo(() => {
        if (status !== 'PLAYING') return new Set<string>();
        const set = new Set<string>();

        for (const ticket of tickets) {
            if (!ticket.grid || ticket.grid.length === 0) continue;

            const hitsSet = new Set(ticket.hits ?? []);
            const rowMissing: number[] = [];

            for (const row of ticket.grid) {
                if (!row) continue;
                const numbersInRow = row.filter((n): n is number => n !== null && n !== 0);
                if (numbersInRow.length === 0) continue;
                const unhitCount = numbersInRow.filter(n => !hitsSet.has(n)).length;
                rowMissing.push(unhitCount);
            }

            if (rowMissing.length === 0) continue;
            rowMissing.sort((x, y) => x - y);

            let isNearWin = false;
            // Sunday Gala logic relies on figures, but row logic is a safe approximation for now
            if (type === 'SUNDAY_GALA') {
                let totalMissing = 0;
                for (const m of rowMissing) { totalMissing += m; }
                isNearWin = totalMissing === 1;
            } else {
                if (!lineAwarded) {
                    isNearWin = (rowMissing[0] ?? 99) === 1;
                } else if (!twoLinesAwarded) {
                    if (rowMissing.length >= 2) {
                        isNearWin = ((rowMissing[0] ?? 99) + (rowMissing[1] ?? 99)) === 1;
                    }
                } else {
                    let totalMissing = 0;
                    for (const m of rowMissing) { totalMissing += m; }
                    isNearWin = totalMissing === 1;
                }
            }

            if (isNearWin) {
                set.add(ticket.id);
            }
        }
        return set;
    }, [status, tickets, type, lineAwarded, twoLinesAwarded]);

    // Sorted tickets for rendering based on phase progression
    const sortedTickets = useMemo(() => {
        return [...tickets].sort((a, b) => {
            const getMissingBallsForPhase = (ticket: typeof tickets[0]) => {
                if (!ticket.grid || ticket.grid.length === 0) return 99;

                const hitsSet = new Set(ticket.hits ?? []);
                const rowMissing: number[] = [];

                for (const row of ticket.grid) {
                    if (!row) continue;
                    const numbersInRow = row.filter((n): n is number => n !== null && n !== 0);
                    if (numbersInRow.length === 0) continue;
                    const unhitCount = numbersInRow.filter(n => !hitsSet.has(n)).length;
                    rowMissing.push(unhitCount);
                }

                if (rowMissing.length === 0) return 99;
                rowMissing.sort((x, y) => x - y);

                if (type === 'SUNDAY_GALA') {
                    let totalMissing = 0;
                    for (const m of rowMissing) { totalMissing += m; }
                    return totalMissing;
                }

                if (!lineAwarded) {
                    return rowMissing[0] ?? 99;
                }
                if (!twoLinesAwarded) {
                    if (rowMissing.length >= 2) return (rowMissing[0] ?? 99) + (rowMissing[1] ?? 99);
                    return rowMissing[0] ?? 99;
                }

                let totalMissing = 0;
                for (const m of rowMissing) { totalMissing += m; }
                return totalMissing;
            };

            const missingA = getMissingBallsForPhase(a) ?? 99;
            const missingB = getMissingBallsForPhase(b) ?? 99;

            // The one with the LEAST missing balls for the current phase goes FIRST
            if (missingA !== missingB) {
                return missingA - missingB;
            }

            // Tie breaker: most total hits
            return (b.hits?.length ?? 0) - (a.hits?.length ?? 0);
        });
    }, [tickets, type, lineAwarded, twoLinesAwarded]);

    // === SOCKET CONNECTION ===
    // Use joinGame from gameStore which properly connects, sets up listeners, and joins room
    useEffect(() => {
        if (sessionId && isAuthenticated && user) {
            joinGame(user.id);
        }
    }, [sessionId, isAuthenticated, user, joinGame]);

    // Additional socket event listeners (win, game lifecycle from GamePage perspective)
    useEffect(() => {
        const socket = socketClient.getSocket();
        if (!socket) return;

        const onWin = (payload: Record<string, unknown>) => {
            const prize = Number(payload['prize'] ?? 0);
            const wType = String(payload['winType'] ?? 'BINGO');
            const wUserId = String(payload['userId']);
            let formattedWinType = wType.replace('_', ' ');

            const isSunday = useGameStore.getState().type === 'SUNDAY_GALA';
            if (isSunday) {
                if (wType === 'LINE') formattedWinType = t('game.figureComplete');
                if (wType === 'BINGO') formattedWinType = t('game.blackout');
            }

            // Read user from the store at EVENT TIME, not closure time
            const currentUser = useAuthStore.getState().user;

            if (currentUser && wUserId === currentUser.id) {
                // The current user won
                setWinAmount(prize);
                setWinType(formattedWinType);
                setShowWinModal(true);
                if (settings.vibration && navigator.vibrate) {
                    navigator.vibrate([200, 100, 200, 100, 400]);
                }
                refreshBalance();
            } else {
                // Someone else won
                setGlobalToast({
                    message: t('game.toastSomeoneWon', { type: formattedWinType }),
                    prize,
                    isVisible: true,
                });

                setTimeout(() => {
                    setGlobalToast(prev => ({ ...prev, isVisible: false }));
                }, 3000);
            }

            // Instantly sync the local state so sorting reacts mid-game
            if (wType === 'LINE') useGameStore.getState().setPhaseAwarded('LINE');
            if (wType === 'TWO_LINES') useGameStore.getState().setPhaseAwarded('TWO_LINES');
        };

        const onGameStarted = () => {
            fetchMyTickets();
        };

        const onGameEnded = () => {
            fetchMissions();
        };

        socket.on('win', onWin);
        socket.on('game_started', onGameStarted);
        socket.on('game_ended', onGameEnded);

        return () => {
            socket.off('win', onWin);
            socket.off('game_started', onGameStarted);
            socket.off('game_ended', onGameEnded);
        };
    }, [refreshBalance, fetchMissions, fetchMyTickets, settings.vibration, user?.id, isConnected]);

    // Join room when socket becomes available (reconnect scenario)
    useEffect(() => {
        const socket = socketClient.getSocket();
        if (!socket || !sessionId || !user) return;

        const onReconnect = () => {
            socketClient.joinRoom(sessionId, user.id);
            fetchGameState();
            fetchMyTickets();
        };

        socket.on('connect', onReconnect);
        return () => { socket.off('connect', onReconnect); };
    }, [sessionId, user, fetchGameState, fetchMyTickets]);

    // Fetch state
    useEffect(() => {
        fetchGameState();
        const interval = setInterval(() => {
            if (status === 'WAITING' || status === 'LOADING') {
                fetchGameState();
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [fetchGameState, status]);

    // Fetch tickets
    useEffect(() => {
        if (isAuthenticated) fetchMyTickets();
    }, [isAuthenticated, fetchMyTickets]);

    // Missed game detection
    useEffect(() => {
        if (status === 'PLAYING' && tickets.length === 0) {
            setMissedGame(true);
        } else {
            setMissedGame(false);
        }
    }, [status, tickets.length]);

    // Near-win haptic
    useEffect(() => {
        if (nearWinTicketIds.size > 0 && settings.vibration && navigator.vibrate) {
            navigator.vibrate(100);
        }
    }, [nearWinTicketIds.size, settings.vibration]);

    // === AD-BASED TICKET SYSTEM ===
    const [adClaimedThisRound, setAdClaimedThisRound] = useState(false);

    const handleWatchAdForTicket = useCallback(async () => {
        if (adClaimedThisRound) return; // Prevent double-fire
        setAdClaimedThisRound(true);
        try {
            const result = await watchAd('game_single');
            if (result.success) {
                addEarnedCards(1);
                showToast(t('game.toastCardEarned'), 'success');
            } else {
                showToast(t('game.toastAdNotAvailable'), 'error');
            }
        } finally {
            // Re-enable after a short delay to prevent rapid double-taps
            setTimeout(() => setAdClaimedThisRound(false), 2000);
        }
    }, [watchAd, addEarnedCards, showToast, adClaimedThisRound]);

    const handleWatchAdBundle = useCallback(async () => {
        if (bundleUsedThisSession) {
            showToast(t('game.toastChestUsed'), 'error');
            return;
        }
        const result = await watchAd('game_bundle');
        if (!result.success) return;

        const newProgress = bundleProgress + 1;
        if (newProgress >= 3) {
            // Chest complete! Add 5 earned cards
            addEarnedCards(5);
            setBundleProgress(3); // Keep it at 3 so UI shows completed state
            markBundleUsed();
            setChestJustCompleted(true);
            showToast(t('game.toastChestUnlocked'), 'success');
            setTimeout(() => setChestJustCompleted(false), 3000);
        } else {
            setBundleProgress(newProgress);
        }
    }, [watchAd, bundleProgress, bundleUsedThisSession, showToast, addEarnedCards, setBundleProgress, markBundleUsed]);

    // === RESCUE FLOW (Golden Path 3) ===
    const handleRedeemWithChips = useCallback(async () => {
        if (chips < 500) {
            setShowRescueSheet(true);
            return;
        }
        try {
            await buyTicket(true);
            await fetchMyTickets();  // Ensure UI is synced
            await refreshBalance();
            showToast(t('game.toastCardRedeemed'), 'success');
        } catch {
            showToast(t('game.toastFailedRedeem'), 'error');
        }
    }, [chips, buyTicket, fetchMyTickets, refreshBalance, showToast]);

    const handleRescueAd = useCallback(async () => {
        const result = await watchAd('game_single');
        if (result.success) {
            addEarnedCards(1);
            setShowRescueSheet(false);
            showToast(t('game.toastFreeCardEarned'), 'success');
        }
    }, [watchAd, addEarnedCards, showToast]);

    // Card equip: buy tickets from server for each earned card
    const handleEquipCards = useCallback(async () => {
        try {
            const count = earnedCards;
            for (let i = 0; i < count; i++) {
                await buyTicket();
            }
            resetEarnedCards();
            await fetchMyTickets();
            await refreshBalance();
            showToast(count > 1 ? t('game.toastCardsEquipped', { count }) : t('game.toastCardEq', { count }), 'success');
        } catch {
            showToast(t('game.toastErrorGettingCards'), 'error');
        }
    }, [buyTicket, refreshBalance, fetchMyTickets, showToast, resetEarnedCards, earnedCards]);

    // Exit
    const handleExit = () => {
        setShowExitModal(false);
        onNavigate('home');
    };

    // Post-game loop: back to lobby (NOT home)
    const handlePrepareNext = () => {
        setShowWinModal(false);
        refreshBalance();
        // Stay on game page — the status will change to WAITING automatically
    };

    // Nav
    const handleNavAction = (tab: string) => {
        const validViews = ['game', 'store', 'sunday', 'home', 'missions', 'referrals', 'profile'] as const;
        type ValidView = typeof validViews[number];
        if (tab === 'game') return;
        if (validViews.includes(tab as ValidView)) {
            onNavigate(tab as ValidView);
        } else {
            onNavigate('home');
        }
    };

    const socket = socketClient.getSocket();
    const socketConnected = socket?.connected ?? false;

    // Timer
    const timerMin = Math.floor(dailyTimer / 60);
    const timerSec = dailyTimer % 60;

    return (
        <div className="min-h-screen bg-purple-gradient">
            {/* === HEADER === */}
            <header className="px-4 pt-6 pb-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setShowExitModal(true)}
                        className="w-10 h-10 rounded-full bg-white/10 border border-white/15 flex items-center justify-center hover:bg-white/20 transition-colors"
                    >
                        <Icon name="arrow_back" className="text-white" />
                    </motion.button>
                    <div>
                        <span className="text-[10px] font-bold text-primary uppercase tracking-[0.3em]">{t('home.bingoton')}</span>
                        <h1 className="text-lg font-bold text-white font-display">
                            {type === 'SUNDAY_GALA' ? t('game.sundayGala') : t('game.dailyBingo')}
                        </h1>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 bg-white/10 px-3 py-1.5 rounded-pill border border-white/10">
                        <div className="w-4 h-4"><PerfectCoin /></div>
                        <span className="text-sm font-bold text-primary">{animatedChips.toLocaleString()}</span>
                    </div>
                </div>
            </header>

            <main className="pt-2 pb-32 px-4 max-w-lg mx-auto">
                {/* Connection & players */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${socketConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                        <span className="text-sm text-white/50">
                            {socketConnected ? t('game.live') : t('game.connecting')}
                        </span>
                    </div>
                    <div className="text-sm text-white/50">
                        👥 {playerCount}
                    </div>
                </div>

                {/* Game Timer */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                >
                    <GameTimer timeUntilStart={timeUntilStart} status={status} />
                </motion.section>

                {/* Ball Display (when playing) */}
                {(status === 'PLAYING' || drawnNumbers.length > 0) && (
                    <motion.section
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mb-6"
                    >
                        <BallDisplay lastNumber={lastDrawnNumber} drawnNumbers={drawnNumbers} />
                    </motion.section>
                )}

                {/* === MISSED GAME === */}
                {missedGame && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-12 bg-white rounded-3xl shadow-card-white mb-6"
                    >
                        <span className="text-6xl mb-4 block">😅</span>
                        <p className="text-[#1A0B2E] font-bold mb-2 font-display">{t('game.youMissedIt')}</p>
                        <p className="text-sm text-[#1A0B2E]/50 mb-4">
                            {t('game.grabBeforeNext')}
                        </p>
                        <div className="text-2xl font-mono font-bold text-[#6D28D9]">
                            {String(timerMin).padStart(2, '0')}:{String(timerSec).padStart(2, '0')}
                        </div>
                    </motion.div>
                )}

                {/* === LOBBY: CARD ACQUISITION (Thumb Zone) === */}
                {status === 'WAITING' && (
                    <motion.section
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mb-6 space-y-4"
                    >
                        {/* Prize breakdown — eye-catching */}
                        {/* Prize breakdown — eye-catching */}
                        {type === 'SUNDAY_GALA' ? (
                            <div className="bg-gradient-to-br from-[#FFC107]/20 to-[#FFD54F]/10 border border-[#FFC107]/30 rounded-2xl p-5 shadow-[0_0_20px_rgba(255,193,7,0.15)]">
                                <h3 className="text-sm font-black text-[#FFC107] uppercase tracking-widest mb-4 text-center flex items-center justify-center gap-2">
                                    <span className="text-lg">🌟</span>
                                    {t('game.sundayPrizes')}
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">🎨</span>
                                            <span className="text-sm font-bold text-white">{t('game.figureComplete')}</span>
                                        </div>
                                        <span className="text-sm font-bold text-[#FFC107]">{t('game.sharedPot')}</span>
                                    </div>
                                    <div className="flex items-center justify-between bg-gradient-to-r from-[#FFC107]/10 to-[#F59E0B]/10 border border-[#FFC107]/20 rounded-xl px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">🎉</span>
                                            <span className="text-sm font-black text-[#FFC107]">{t('game.blackout')}</span>
                                        </div>
                                        <span className="text-lg font-black text-[#FFC107] animate-pulse flex items-center gap-1.5">{useGameStore.getState().sundayPrizePool.toLocaleString()} <div className="w-6 h-6"><PerfectCoin /></div></span>
                                    </div>
                                </div>
                                <p className="text-[10px] text-white/40 text-center mt-3">{t('game.tiesSplit')}</p>
                            </div>
                        ) : (
                            <div className="bg-gradient-to-br from-[#1E1150]/80 to-[#2D1B69]/80 border border-[#FBBF24]/30 rounded-2xl p-5 shadow-[0_0_20px_rgba(251,191,36,0.15)]">
                                <h3 className="text-sm font-black text-[#FBBF24] uppercase tracking-widest mb-4 text-center flex items-center justify-center gap-2">
                                    <span className="text-lg">🏆</span>
                                    {t('game.winPrizesToday')}
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">🎯</span>
                                            <span className="text-sm font-bold text-white">{t('game.line1')}</span>
                                        </div>
                                        <span className="text-base font-black text-[#FBBF24] flex items-center gap-1.5">{useGameStore.getState().dailyLinePrize.toLocaleString()} <div className="w-5 h-5"><PerfectCoin /></div></span>
                                    </div>
                                    <div className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">🎯🎯</span>
                                            <span className="text-sm font-bold text-white">{t('game.line2')}</span>
                                        </div>
                                        <span className="text-base font-black text-[#FBBF24] flex items-center gap-1.5">{useGameStore.getState().dailyTwoLinesPrize.toLocaleString()} <div className="w-5 h-5"><PerfectCoin /></div></span>
                                    </div>
                                    <div className="flex items-center justify-between bg-gradient-to-r from-[#FBBF24]/10 to-[#F59E0B]/10 border border-[#FBBF24]/20 rounded-xl px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">🎉</span>
                                            <span className="text-sm font-black text-[#FBBF24]">{t('game.bingo')}</span>
                                        </div>
                                        <span className="text-lg font-black text-[#FBBF24] animate-pulse flex items-center gap-1.5">{useGameStore.getState().dailyBingoPrize.toLocaleString()} <div className="w-5 h-5"><PerfectCoin /></div></span>
                                    </div>
                                </div>
                                <p className="text-[10px] text-white/40 text-center mt-3">{t('game.tiesSplit')}</p>
                            </div>
                        )}
                        {/* Cards earned indicator — CLICKABLE to open card selector */}
                        {earnedCards > 0 && (
                            <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={handleEquipCards}
                                className="w-full text-center py-3 bg-white rounded-3xl shadow-card-white cursor-pointer hover:bg-white/90 transition-colors"
                            >
                                <span className="text-[#1A0B2E] font-black text-sm">
                                    {earnedCards === 1 ? t('game.cardReady', { count: earnedCards }) : t('game.cardsReady', { count: earnedCards })}
                                </span>
                            </motion.button>
                        )}

                        {/* Single ad → 1 card */}
                        <Button
                            onClick={handleWatchAdForTicket}
                            disabled={adWatchingPlacement !== null}
                            isLoading={adWatchingPlacement === 'game_single'}
                            variant="secondary"
                            icon="play_circle"
                            fullWidth
                            size="md"
                        >
                            {t('game.claimFreeCard')}
                        </Button>

                        {/* Chest/Key bundle */}
                        <ChestUnlock
                            progress={bundleProgress}
                            isLoading={adWatchingPlacement === 'game_bundle'}
                            onWatchAd={handleWatchAdBundle}
                            rewardCards={5}
                            justCompleted={chestJustCompleted}
                        />

                        {/* BingoChips redeem */}
                        <Button
                            onClick={handleRedeemWithChips}
                            variant="ghost"
                            icon="toll"
                            fullWidth
                            size="sm"
                        >
                            {t('game.buyCard')}
                        </Button>

                        {/* Select & equip cards button (always visible when cards earned) */}
                        {earnedCards > 0 && (
                            <Button
                                onClick={handleEquipCards}
                                fullWidth
                                icon="grid_view"
                                size="lg"
                            >
                                {t('game.equipCardsBtn', { count: earnedCards })}
                            </Button>
                        )}

                        {/* Show existing server tickets too */}
                        {tickets.length > 0 && earnedCards === 0 && (
                            <div className="text-center py-3 bg-green-500/10 border border-green-500/20 rounded-3xl">
                                <span className="text-green-400 font-bold text-sm">
                                    {tickets.length === 1 ? t('game.cardEquipped', { count: tickets.length }) : t('game.cardsEquipped', { count: tickets.length })}
                                </span>
                            </div>
                        )}
                    </motion.section>
                )}



                {/* === MY CARDS === */}
                {tickets.length > 0 && (
                    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <h2 className="text-lg font-bold text-white mb-4 font-display">
                            {t('game.myCards', { count: tickets.length })}
                        </h2>
                        <div className="space-y-4">
                            {sortedTickets.length > 0 && sortedTickets[0]?.grid?.length === 5 ? (
                                // 5x5 Grid format (Bingo 75 / Sunday Gala)
                                sortedTickets.map((ticket, idx) => {
                                    const isNearWin = nearWinTicketIds.has(ticket.id);
                                    return (
                                        <motion.div
                                            key={ticket.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{
                                                opacity: 1,
                                                y: 0,
                                                boxShadow: isNearWin
                                                    ? '0 0 20px rgba(251,191,36,0.5), 0 0 40px rgba(251,191,36,0.2)'
                                                    : 'none',
                                            }}
                                            transition={{ delay: idx * 0.1 }}
                                            className={`rounded-3xl ${isNearWin ? 'ring-2 ring-primary/50' : ''}`}
                                        >
                                            {isNearWin && (
                                                <div className="text-center py-1.5 text-xs font-black text-primary animate-pulse bg-primary/10 rounded-t-3xl">
                                                    {t('game.almostThere')}
                                                </div>
                                            )}
                                            {ticket.grid && ticket.grid.length > 0 ? (
                                                <BingoCard
                                                    grid={ticket.grid}
                                                    hits={ticket.hits ?? []}
                                                    isPlaying={status === 'PLAYING'}
                                                    ticketId={ticket.id.substring(0, 5).toUpperCase()}
                                                />
                                            ) : (
                                                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                                                    <div className="animate-pulse space-y-2">
                                                        {[...Array(5)].map((_, r) => (
                                                            <div key={r} className="flex gap-1 justify-center">
                                                                {[...Array(5)].map((_, c) => (
                                                                    <div key={c} className="w-8 h-8 bg-white/10 rounded" />
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <p className="text-white/30 text-xs mt-2">{t('game.loadingCard')}</p>
                                                </div>
                                            )}
                                        </motion.div>
                                    );
                                })
                            ) : (
                                // 3x9 Grid format (Bingo 90 / Daily 15)
                                <Bingo90Strip
                                    tickets={sortedTickets as any}
                                    drawnNumbers={new Set(drawnNumbers)}
                                />
                            )}
                        </div>
                    </motion.section>
                )}

                {/* Empty state — NO_SESSION fallback */}
                {tickets.length === 0 && !missedGame && status !== 'PLAYING' && status !== 'WAITING' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-12 px-6 bg-white rounded-3xl shadow-card-white"
                    >
                        <div className="w-12 h-12 mx-auto mb-6 border-4 border-[#6D28D9] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-[#1A0B2E] font-bold mb-2 text-lg font-display">{t('game.preparingNext')}</p>
                        <p className="text-sm text-[#1A0B2E]/60 mb-6">
                            {t('game.serverSettingUp')}
                        </p>
                        <Button
                            onClick={() => fetchGameState()}
                            variant="secondary"
                            size="md"
                            fullWidth
                        >
                            {t('game.refreshConnection')}
                        </Button>
                    </motion.div>
                )}
            </main>

            {/* Bottom Nav */}
            <BottomNav activeTab="game" onTabChange={handleNavAction} />

            {/* Exit Modal */}
            <ExitGameModal
                isOpen={showExitModal}
                onClose={() => setShowExitModal(false)}
                onConfirm={handleExit}
            />

            {/* Win Modal — post-game loop */}
            <AnimatePresence>
                {showWinModal && (
                    <WinModal
                        isOpen={showWinModal}
                        amount={winAmount}
                        title={winType}
                        currency="BingoChips"
                        onClose={() => setShowWinModal(false)}
                        onClaim={() => {
                            setShowWinModal(false);
                            refreshBalance();
                        }}
                        onPrepareNext={handlePrepareNext}
                    />
                )}
            </AnimatePresence>

            <GlobalWinToast
                message={globalToast.message}
                prize={globalToast.prize}
                isVisible={globalToast.isVisible}
                currency="BingoChips"
            />

            {/* === RESCUE BOTTOM SHEET (Golden Path 3) === */}
            <BottomSheet
                isOpen={showRescueSheet}
                onClose={() => setShowRescueSheet(false)}
                title={t('game.oopsOut')}
            >
                <p className="text-[#1A0B2E]/60 text-sm text-center mb-6">
                    {t('game.grabBeforeStarts')}
                </p>
                <Button
                    onClick={handleRescueAd}
                    isLoading={adWatchingPlacement === 'game_single'}
                    fullWidth
                    icon="play_circle"
                    size="lg"
                >
                    {t('game.watch1Free')}
                </Button>
                <div className="mt-3">
                    <Button
                        onClick={() => {
                            setShowRescueSheet(false);
                            onNavigate('missions');
                        }}
                        variant="ghost"
                        fullWidth
                        size="sm"
                    >
                        {t('game.goToMissions')}
                    </Button>
                </div>
            </BottomSheet>

            {/* Toast */}
            <Toast toast={toast} onDismiss={dismissToast} />
        </div>
    );
}
